// src/components/AIAnalysis/AnalysisHistory.tsx
import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { FileText } from 'lucide-react'; // Changed from Format to FileText which exists in lucide-react

interface AnalysisHistoryItem {
  id: string;
  created_at: string;
  timeframe: string;
  analysis: string;
}

interface AnalysisHistoryProps {
  onSelectAnalysis: (id: string) => void;
}

const AnalysisHistory: React.FC<AnalysisHistoryProps> = ({ onSelectAnalysis }) => {
  const [history, setHistory] = useState<AnalysisHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchHistory() {
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from('analysis_history')
          .select('id, created_at, timeframe, analysis')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        
        setHistory(data || []);
      } catch (err: any) {
        setError(err.message || 'Failed to load analysis history');
        console.error('Error fetching analysis history:', err);
      } finally {
        setLoading(false);
      }
    }
    
    fetchHistory();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatTimeframe = (timeframe: string) => {
    switch (timeframe) {
      case 'today': return 'Today';
      case 'last7days': return 'Last 7 Days';
      case 'last30days': return 'Last 30 Days';
      case 'lastQuarter': return 'Last Quarter';
      case 'allTime': return 'All Time';
      default: return timeframe;
    }
  };

  if (loading) return <div className="text-center py-4">Loading analysis history...</div>;
  
  if (error) return <div className="text-red-500 py-4">{error}</div>;
  
  if (history.length === 0) {
    return <div className="text-gray-500 py-4 text-center">No previous analyses found.</div>;
  }

  return (
    <div className="mt-8">
      <h3 className="text-xl font-semibold mb-4">Previous Analyses</h3>
      <div className="space-y-4">
        {history.map((item) => (
          <Card key={item.id} className="p-4">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-sm text-gray-500">{formatDate(item.created_at)}</div>
                <div className="font-medium">{formatTimeframe(item.timeframe)}</div>
                <div className="mt-2 text-sm text-gray-700 line-clamp-2">{item.analysis.substring(0, 150)}...</div>
              </div>
              <Button 
                onClick={() => onSelectAnalysis(item.id)} 
                variant="ghost" 
                size="sm" 
                className="flex items-center"
              >
                <FileText size={16} className="mr-1" />
                View
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AnalysisHistory;